import { Box, Button, Divider } from "@chakra-ui/react";
import { ActionPromptContainer, Error, LeadTypeTag, Loader } from "components";
import { observer } from "mobx-react";
import React, {
  FC,
  KeyboardEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { TLeadReapitSyncData } from "types/reapit.type";
import { AccountLeadStore } from "store/UserAccounts/UserAccount/AccountLeads/AccountLead.store";
import {
  getEcosystemReapitSegmentedAddress,
  getLocalReapitSegmentedAddress,
} from "utils/reapit.utils";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { LeadTypeEnum } from "enums/lead-type.enum";
import { ApiRequestStatusEnum } from "enums/api-request-status.enum";
import { AccountIntegrationReapitAreaStore } from "store/UserAccounts/UserAccount/AccountIntegrations/AccountIntegrationReapit/AccountIntegrationReapitAreas.store";
import { AccountIntegrationReapitContactsStore } from "store/UserAccounts/UserAccount/AccountIntegrations/AccountIntegrationReapit/AccountIntegrationReapitContacts.store";
import { getUserFullName } from "utils/account-user.utils";
import { RentFrequencyEnum } from "enums/rent-frequency.enum";
import { range } from "lodash";
import { reapitSyncValidationSchema } from "utils/validation-schemas/reapit-sync.validation";
import { TReapitSyncFormFieldValues as TFieldValues } from "./reapit-sync-form-field.type";
import { LeadAddressForm } from "./components/LeadAddressForm";
import { ApplicantRequirementsForm } from "./components/ApplicantRequirementsForm";
import { ExistingRecordsForm } from "./components/ExistingRecordsForm";
import { NotesForm } from "./components/NotesForm";

interface IProps {
  leadStore: AccountLeadStore;
  handleSyncLead: (
    syncData: TLeadReapitSyncData,
    officeGroupId?: number
  ) => Promise<void>;
  closePrompt: () => void;
  officeGroupId?: number;
  areasStore: AccountIntegrationReapitAreaStore;
  contactsStore: AccountIntegrationReapitContactsStore;
  isApplicantRequirementsEnabled: boolean;
  areApplicantRequirementsFieldsMandatory: boolean;
}

export const LeadReapitSyncFormPrompt: FC<IProps> = observer(
  ({
    leadStore,
    handleSyncLead,
    closePrompt,
    areasStore,
    contactsStore,
    officeGroupId,
    isApplicantRequirementsEnabled,
    areApplicantRequirementsFieldsMandatory,
  }) => {
    const {
      address,
      postcode,
      type,
      id: leadId,
      person,
      ecosystemAddress,
    } = leadStore.lead;
    const { validateLeadAddressOnEcosystem } = leadStore;

    const { fetchReapitAccountAreas, reapitAccountAreasOptions } = areasStore;
    const {
      fetchReapitAccountContacts,
      reapitRelatedContactsOptions,
    } = contactsStore;
    const [loadingStatus, setLoadingStatus] = useState(
      ApiRequestStatusEnum.NONE
    );
    const [ecosystemLoadingState, setEcosystemLoadingState] = useState<
      ApiRequestStatusEnum
    >(ApiRequestStatusEnum.NONE);

    const transformedAddress = useMemo(() => {
      if (ecosystemLoadingState === ApiRequestStatusEnum.ERROR) {
        return getLocalReapitSegmentedAddress(address, postcode);
      }
      if (!ecosystemAddress) return;
      return getEcosystemReapitSegmentedAddress(
        ecosystemAddress,
        address,
        postcode
      );
    }, [ecosystemAddress, address, postcode, ecosystemLoadingState]);

    const priceArray: number[] = useMemo(() => {
      let prices = [];
      if (type === LeadTypeEnum.Sale) {
        prices.push(...range(50000, 300000, 10000));
        prices.push(...range(300000, 1050000, 50000));
        prices.push(
          1250000,
          1500000,
          1750000,
          2000000,
          2500000,
          3000000,
          3500000,
          4000000,
          5000000
        );
      }
      if (type === LeadTypeEnum.Let) {
        prices.push(...range(100, 5100, 100));
        prices.push(...range(6000, 11000, 1000));
        prices.push(...range(20000, 60000, 10000));
      }
      return prices;
    }, [type]);

    const isApplicant = [LeadTypeEnum.Let, LeadTypeEnum.Sale].includes(type);

    const showApplicantRequirements =
      isApplicant && isApplicantRequirementsEnabled;

    useEffect(() => {
      const fetchData = async () => {
        try {
          setEcosystemLoadingState(ApiRequestStatusEnum.PENDING);
          await validateLeadAddressOnEcosystem();
          setEcosystemLoadingState(ApiRequestStatusEnum.SUCCESS);
        } catch (error) {
          setEcosystemLoadingState(ApiRequestStatusEnum.ERROR);
        }
      };
      fetchData();
    }, [validateLeadAddressOnEcosystem]);

    useEffect(() => {
      const fetchData = async () => {
        try {
          setLoadingStatus(ApiRequestStatusEnum.PENDING);
          await fetchReapitAccountContacts(leadId, officeGroupId);
          if (showApplicantRequirements) {
            await fetchReapitAccountAreas(leadId);
          }
          setLoadingStatus(ApiRequestStatusEnum.SUCCESS);
        } catch (error) {
          setLoadingStatus(ApiRequestStatusEnum.ERROR);
        }
      };

      fetchData();
    }, [
      fetchReapitAccountAreas,
      fetchReapitAccountContacts,
      leadId,
      officeGroupId,
      type,
      isApplicantRequirementsEnabled,
      showApplicantRequirements,
    ]);

    const initialValues: TFieldValues = useMemo(() => {
      return {
        buildingName: "",
        buildingNumber: "",
        line1: "",
        line2: "",
        line3: "",
        line4: "",
        postcode: "",
        locationOptions: [],
        minPrice: null,
        maxPrice: null,
        contactId: reapitRelatedContactsOptions[0]?.value,
        notes: "",
      };
    }, [reapitRelatedContactsOptions]);

    const validationSchema = useMemo(() => {
      return reapitSyncValidationSchema(
        isApplicant,
        showApplicantRequirements,
        areApplicantRequirementsFieldsMandatory,
        reapitAccountAreasOptions
      );
    }, [
      reapitAccountAreasOptions,
      showApplicantRequirements,
      isApplicant,
      areApplicantRequirementsFieldsMandatory,
    ]);

    const personFullName = useMemo(() => {
      return (
        person?.fullName ||
        getUserFullName(person?.firstName, person?.lastName) ||
        ""
      );
    }, [person]);

    const handleKeydown = (event: KeyboardEvent) => {
      const isTextarea = event.target instanceof HTMLTextAreaElement;
      if (event.key === "Enter" && !isTextarea) {
        event.preventDefault();
      }
    };

    const {
      handleSubmit,
      control,
      formState: { isSubmitting },
      setValue,
      getValues,
      watch,
      reset,
    } = useForm<TFieldValues>({
      defaultValues: initialValues,
      mode: "onSubmit",
      resolver: yupResolver(validationSchema),
    });

    useEffect(() => {
      setValue("contactId", reapitRelatedContactsOptions[0]?.value);
    }, [reapitRelatedContactsOptions, setValue]);

    useEffect(() => {
      const current = getValues();
      reset(
        {
          ...current,
          ...transformedAddress,
        },
        { keepDirtyValues: true }
      );
    }, [reset, getValues, transformedAddress]);

    const onSubmit = useCallback(
      async ({
        locationOptions,
        contactId,
        minPrice: formMinPrice,
        maxPrice: formMaxPrice,
        notes,
        ...rest
      }: TFieldValues) => {
        const formMinPriceAsNumber = formMinPrice ?? 0;
        const formMaxPriceAsNumber = formMaxPrice ?? 0;

        const syncData: TLeadReapitSyncData = {
          locationOptions,
          contactId: contactId === "newRecord" ? null : contactId,
          address: rest,
          notes: notes?.trim() || undefined,
        };

        const shouldSendApplicantRequirementsData =
          areApplicantRequirementsFieldsMandatory ||
          formMinPriceAsNumber ||
          formMaxPriceAsNumber;
        if (
          isApplicantRequirementsEnabled &&
          shouldSendApplicantRequirementsData
        ) {
          if (type === LeadTypeEnum.Sale) {
            syncData.buying = {
              priceFrom: formMinPriceAsNumber,
              priceTo: formMaxPriceAsNumber,
            };
          } else if (type === LeadTypeEnum.Let) {
            syncData.renting = {
              rentFrom: formMinPriceAsNumber,
              rentTo: formMaxPriceAsNumber,
              rentFrequency: RentFrequencyEnum.MONTHLY,
            };
          }
        }

        await handleSyncLead(syncData, officeGroupId);
        closePrompt();
      },
      [
        handleSyncLead,
        closePrompt,
        officeGroupId,
        type,
        isApplicantRequirementsEnabled,
        areApplicantRequirementsFieldsMandatory,
      ]
    );

    return (
      <ActionPromptContainer
        header={<Box color={"leadpro.600"}>Sync lead to Reapit</Box>}
        bodyStyle={{ padding: "0" }}
        body={
          <Box minHeight={"100px"} position={"relative"}>
            {loadingStatus === ApiRequestStatusEnum.PENDING && <Loader />}
            {loadingStatus === ApiRequestStatusEnum.ERROR && <Error />}
            {loadingStatus === ApiRequestStatusEnum.SUCCESS && (
              <Box>
                <Box
                  px={8}
                  py={5}
                  display={"flex"}
                  justifyContent={"space-between"}
                  alignItems={"center"}
                >
                  <Box>
                    {personFullName}{" "}
                    <Box display={"inline"} color={"leadpro.500"}>
                      ({person.email})
                    </Box>
                  </Box>
                  <LeadTypeTag type={type} />
                </Box>
                <hr />
                <Box px={8} pb={8}>
                  <form
                    onSubmit={handleSubmit(onSubmit)}
                    onKeyDown={handleKeydown}
                  >
                    <LeadAddressForm
                      control={control}
                      isApplicant={isApplicant}
                      isValidating={
                        ecosystemLoadingState === ApiRequestStatusEnum.PENDING
                      }
                    />
                    {showApplicantRequirements && (
                      <ApplicantRequirementsForm
                        control={control}
                        priceArray={priceArray}
                        reapitAccountAreasOptions={reapitAccountAreasOptions}
                        areApplicantRequirementsFieldsMandatory={
                          areApplicantRequirementsFieldsMandatory
                        }
                        type={type}
                        watch={watch}
                      />
                    )}
                    <ExistingRecordsForm
                      control={control}
                      reapitRelatedContactsOptions={
                        reapitRelatedContactsOptions
                      }
                    />

                    <NotesForm control={control} />

                    <Divider mb={4} />
                    <Box
                      width={"100%"}
                      display={"flex"}
                      flexDirection={"row"}
                      justifyContent={"flex-end"}
                    >
                      <Button
                        type={"submit"}
                        colorScheme={"blue"}
                        isDisabled={isSubmitting}
                        isLoading={isSubmitting}
                      >
                        Sync to Reapit
                      </Button>
                    </Box>
                  </form>
                </Box>
              </Box>
            )}
          </Box>
        }
      />
    );
  }
);
